<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import MessageApi from 'dashboard/api/inbox/message';

import MessageList from 'dashboard/components-next/message/MessageList.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const currentChat = useMapGetter('getSelectedChat');

const fetchedMessages = ref([]);
const isFetching = ref(false);
const hasFetchedOnce = ref(false);

// Live private messages arriving via ActionCable while this conversation is
// open (getSelectedChat.messages is mutated in place by the ADD_MESSAGE
// mutation) - merged with the REST-fetched history below so notes sent
// before this component mounted are not lost, and new ones show up without
// a page refresh.
const liveMessages = computed(() =>
  (currentChat.value.messages || []).filter(message => message.private)
);

const messages = computed(() => {
  const merged = new Map();
  fetchedMessages.value.forEach(message => merged.set(message.id, message));
  liveMessages.value.forEach(message => merged.set(message.id, message));
  return Array.from(merged.values()).sort(
    (a, b) => a.created_at - b.created_at
  );
});

const isEmpty = computed(
  () => hasFetchedOnce.value && !isFetching.value && messages.value.length === 0
);

const fetchPrivateMessages = async () => {
  isFetching.value = true;
  try {
    const response = await MessageApi.getPrivateMessages({
      conversationId: props.conversationId,
    });
    fetchedMessages.value = response.data?.payload || [];
  } finally {
    isFetching.value = false;
    hasFetchedOnce.value = true;
  }
};

watch(() => props.conversationId, fetchPrivateMessages);
onMounted(fetchPrivateMessages);
</script>

<template>
  <div>
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <p
      v-else-if="isEmpty"
      class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11"
    >
      {{ t('CONVERSATION_SIDEBAR.PRIVATE_NOTES.EMPTY') }}
    </p>
    <MessageList
      v-else
      :messages="messages"
      :current-user-id="currentUser.id"
      compact
      class="max-h-[300px] overflow-y-auto"
    />
  </div>
</template>
